<template>
  <ODialog ref="dialog" hideWhenLoading :maxWidth="800">
    <v-card v-if="row != null">
      <OperationDialogTitle v-bind="row" @close="dismiss" />
      <v-card-text class="pa-3">
        <TechValuesReferenceDialog ref="techValues" />
        <CCValuesReferenceDialog ref="ccValues" />

        <v-form
          ref="theForm"
          v-model="isValid"
          class="d-flex flex-wrap items-start"
        >
          <OAutocomplete
            :readonly="readonly"
            class="w-2/3 pa-3"
            v-model="row.vehicleId"
            label="Veículo"
            placeholder="Selecione..."
            :items="vehicles"
            :rules="[isSet]"
          />

          <ODateTimeInput2
            :readonly="readonly"
            class="w-1/3 pa-3"
            label="Data/hora"
            v-model="row.timestamp"
            :clearable="false"
          />

          <OAutocomplete
            :readonly="readonly"
            class="w-2/3 pa-3"
            v-model="row.technicianId"
            label="Técnico"
            placeholder="Selecione..."
            :items="techs"
            :rules="[isSet]"
          />

          <div class="w-1/3 pa-3 d-flex items-center justify-stretch">
            <OMoneyInput
              :readonly="readonly"
              class="grow-1"
              v-model="row.techValue"
              label="Valor p/ técnico"
              :rules="[isSet]"
            />
            <v-btn icon @click="showTechValues">
              <v-icon>mdi-notebook</v-icon>
            </v-btn>
          </div>

          <div class="d-flex items-start w-2/3">
            <div class="d-flex grow-1">
              <OAutocomplete
                :readonly="readonly"
                class="pa-3 w-1/2"
                v-model="row.costCenterId"
                label="Centro de Custo"
                placeholder="Selecione..."
                :items="costCenters"
                :rules="[isSet]"
              />

              <OSelect
                :readonly="readonly"
                class="pa-3 w-1/2"
                label="Produto"
                v-model="row.productTypeId"
                :clearable="false"
                :items="ccProductTypes"
              />
            </div>
          </div>

          <div class="w-1/3 grow-0 shrink-0 pa-3 d-flex items-center">
            <OMoneyInput
              :readonly="readonly"
              class="grow-1"
              v-model="row.costCenterValue"
              label="Valor p/ CC"
              :rules="[isSet]"
            />
            <v-btn icon @click="showCostCenterValues">
              <v-icon>mdi-notebook</v-icon>
            </v-btn>
          </div>

          <OTextarea
            :readonly="readonly"
            class="w-full pa-3"
            label="Comentários"
            v-model="row.comments"
            :rows="2"
          />

          <RowDates class="ma-3" :value="row" />
        </v-form>
      </v-card-text>
      <ODialogButtons
        class="pa-4"
        txtApply="Salvar"
        @apply="apply"
        @cancel="dismiss"
      />
    </v-card>
  </ODialog>
</template>

<script>
import TheUser from 'oderon-vue/auth/TheUser'
import OMoneyInput from 'oderon-vue/cpts/OMoneyInput.vue'
import RowDates from './RowDates.vue'
import OperationDialogTitle from './OperationDialogTitle.vue'
import { ClientHistory, CostCenter, Technician } from '../../../models'
import { isSet } from 'oderon-vue/utils.js'
import TechValuesReferenceDialog from '@/components/values-reference/TechValuesReferenceDialog.vue'
import CCValuesReferenceDialog from '@/components/values-reference/CCValuesReferenceDialog.vue'
import {
  splitAndSort,
  fetchVehiclesOfInterest,
  findActiveInstall,
  postOrPatch,
  canEdit
} from './operation-utils'

export default {
  components: {
    OMoneyInput,
    TechValuesReferenceDialog,
    CCValuesReferenceDialog,
    RowDates,
    OperationDialogTitle
  },
  data() {
    let start = this.reset()
    return {
      ...start,
      isSet,
      techs: [],
      costCenters: [],
      vehicles: [],
      TheUser
    }
  },
  computed: {
    readonly() {
      return this.row && this.row.id && !canEdit(this.TheUser)
    },

    ccProductTypes() {
      let mkNull = (text) => [{ text, value: null, disabled: true }]

      if (this.row.costCenterId == null)
        return mkNull('(Escolha um centro de custo para ver seus produtos...)')

      let cc = this.costCenters.find((x) => x.id == this.row.costCenterId)
      if (cc == null || cc.productTypes == null || cc.productTypes.length < 1)
        return mkNull('Não existem produtos para o CC selecionado')

      let res = cc.productTypes
        .filter((x) => x.type == 'oderon_fox')
        .map((x) => {
          return { ...x, text: x.alias, value: x.id }
        })
      return res
    }
  },
  watch: {
    'row.technicianId'() {
      // não faz autofill se a linha já existia
      if (this.row == null) return null
      if (this.row.id != null) return null
      this.autofillTechValue()
    },
    'row.costCenterId'() {
      // não faz autofill se a linha já existia
      if (this.row == null) return null
      if (this.row.id != null) return null

      // NB: Isto executa depois que a computed-prop
      // ccProductTypes foi atualizada.
      this.autofillProductType()
    },
    'row.productTypeId'() {
      // não faz autofill se a linha já existia
      if (this.row == null) return null
      if (this.row.id != null) return null
      this.autofillCostCenterValue()
    }
  },
  methods: {
    reset() {
      return {
        isValid: false,
        row: { costCenterId: null, productTypeId: null, technicianId: null }
      }
    },

    async open({ clientId, historyId }) {
      Object.assign(this, this.reset())

      if (this.$refs.theForm) this.$refs.theForm.resetValidation()

      this.$refs.dialog.$loading(() => this.fetchAll(clientId, historyId))

      return this.$refs.dialog.$open()
    },

    async fetchAll(clientId, historyId) {
      await Promise.all([
        this.fetchTechs(),
        this.fetchCostCenters(),
        this.fetchVehicles(clientId, historyId)
      ])

      // depois dos anteriores para o autofill inicial funcionar
      await this.fetchData(clientId, historyId)
    },

    async fetchTechs() {
      let items = await Technician.findAll({ includeInactive: true })
      this.techs = splitAndSort(items, {
        prefixToRemove: 'Autorizado Oderon - '
      })
    },

    async fetchCostCenters() {
      let items = await CostCenter.findAll({ includeInactive: true })
      this.costCenters = splitAndSort(items, { prefixToRemove: 'CC ' })
    },

    async fetchVehicles(clientId) {
      this.vehicles = await fetchVehiclesOfInterest(clientId)
    },

    async fetchData(clientId, historyId) {
      // Para retirada, observamos o último install (não assoc)
      // para pré-popular campos.
      if (historyId == null) {
        let activeInstall = await findActiveInstall(clientId)
        this.row = {
          clientId: clientId,
          timestamp: new Date(),
          costCenterValue: null,
          techValue: null,
          type: 'installation',
          subType: 'intervention',
          costCenterId: activeInstall && activeInstall.costCenterId,
          vehicleId: activeInstall && activeInstall.vehicleId,
          productTypeId: activeInstall && activeInstall.productTypeId
        }

        this.autofillTechValue()
        // this.autofillProductType()
        this.autofillCostCenterValue()
      } else {
        this.row = await ClientHistory.findById(historyId)
      }
    },

    // Autofill do techValue quando o tech muda
    // NB: Não existe para intervenção!
    autofillTechValue() {
      // let tech = this.techs.find((x) => x.id == this.row.technicianId)
      // if (tech) {
      //   this.row.techValue = tech.installValue
      //   return true
      // }
    },

    // Autofill do productTypeId quando o CC muda e só tem 1 produto
    // NB: Isto depende da computed-prop ccProductTypes.
    autofillProductType() {
      if (
        this.ccProductTypes.length == 1 &&
        this.ccProductTypes[0].id != null
      ) {
        this.row.productTypeId = this.ccProductTypes[0].id
        return true
      }
    },

    // Autofill do costCenterValue quando o productType muda
    // NB: Não existe para intervenção!
    autofillCostCenterValue() {
      // let cc = this.costCenters.find((x) => x.id == this.row.costCenterId)
      // let types = (cc && cc.productTypes) || []
      // let productType = types.find((x) => x.id == this.row.productTypeId)
      // if (productType != null) {
      //   this.row.costCenterValue = productType.installValue
      //   return true
      // }
    },

    apply() {
      if (this.$refs.theForm.validate()) {
        this.$refs.dialog.$loading(async () => {
          await postOrPatch('/clients/:cid/intervention', this.row)
          this.$snack('Registro salvo com sucesso')
          this.$refs.dialog.$apply('RELOAD')
        })
      }
    },

    dismiss() {
      this.$refs.dialog.$dismiss()
    },

    async showTechValues() {
      let x = await this.$refs.techValues.open(this.row.technicianId)
      if (x != null) this.$set(this.row, 'techValue', x)
    },

    async showCostCenterValues() {
      let x = await this.$refs.ccValues.open(this.row.costCenterId)
      if (x != null) this.$set(this.row, 'costCenterValue', x)
    }
  }
}
</script>

<style></style>
